import React from 'react';
import { Box, Button, Typography } from '@mui/material';

/**
 * Recovers the app when a React.lazy route chunk fails to load.
 *
 * Every authenticated route is behind React.lazy, and the portals are served
 * by nginx with `try_files $uri $uri/ /index.html`. After a redeploy, a tab
 * that is still running the old build asks for a chunk hash that no longer
 * exists; nginx answers 200 with index.html, the browser throws
 * `SyntaxError: Unexpected token '<'`, and webpack follows with a
 * ChunkLoadError. Measured in headless Chrome: with nothing to catch it the
 * whole React tree unmounted and #root was left with 0 children - a blank
 * page. A genuine 404 produced the same blank page, so this is a React-side
 * problem, not an nginx one.
 *
 * Scope is deliberately narrow: ONLY chunk-loading failures are handled here.
 * Any other error is re-thrown from render so ordinary bugs keep behaving
 * exactly as they did before this boundary existed. A boundary that reloaded
 * on every error would turn a render bug into a reload loop.
 */

const RELOAD_MARKER = 'edumetrix_chunk_reload';

// How long the app must run without a chunk failure before the marker is
// cleared. See componentDidMount for why this is not cleared immediately.
const SETTLE_MS = 10000;

export const isChunkLoadError = (error) =>
  !!error &&
  (error.name === 'ChunkLoadError' || /^Loading chunk /.test(String(error.message || '')));

// sessionStorage throws in private mode and when site data is blocked. A
// failure in here must never become a second application failure, so every
// access is guarded and the caller is told whether it worked.
const readMarker = () => {
  try {
    return window.sessionStorage.getItem(RELOAD_MARKER);
  } catch (e) {
    return null;
  }
};

const writeMarker = () => {
  try {
    window.sessionStorage.setItem(RELOAD_MARKER, String(Date.now()));
    return true;
  } catch (e) {
    return false;
  }
};

const clearMarker = () => {
  try {
    window.sessionStorage.removeItem(RELOAD_MARKER);
  } catch (e) {
    /* nothing we can do, and nothing that needs doing */
  }
};

class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { chunkError: null, rethrow: null };
    this.settleTimer = null;
    this.handleRefresh = this.handleRefresh.bind(this);
  }

  static getDerivedStateFromError(error) {
    // Chunk failures are rendered as the recovery UI; everything else is
    // stashed and re-thrown in render, which lets it propagate past this
    // boundary untouched.
    return isChunkLoadError(error)
      ? { chunkError: error, rethrow: null }
      : { chunkError: null, rethrow: error };
  }

  componentDidMount() {
    // The marker is NOT cleared on mount. After an automatic reload this
    // boundary mounts perfectly well - it is the route chunk underneath that
    // fails - so clearing it here would hand the next failure another reload
    // and spin the exact loop the marker exists to prevent. Instead the app
    // has to stay up for a while first; a later redeploy then gets its own
    // fresh reload attempt.
    if (readMarker()) {
      this.settleTimer = setTimeout(clearMarker, SETTLE_MS);
    }
  }

  componentWillUnmount() {
    if (this.settleTimer) clearTimeout(this.settleTimer);
  }

  componentDidCatch(error) {
    if (!isChunkLoadError(error)) return; // render() re-throws these

    // One automatic attempt per tab. A stale index.html is the common cause,
    // and reloading picks up the new one along with the correct chunk hashes.
    if (readMarker()) return; // already tried - fall through to the recovery UI

    // If the marker cannot be persisted we cannot guarantee a single attempt,
    // so do not reload at all and let the user decide instead.
    if (writeMarker()) {
      window.location.reload();
    }
  }

  handleRefresh() {
    window.location.reload();
  }

  render() {
    if (this.state.rethrow) {
      throw this.state.rethrow;
    }

    if (this.state.chunkError) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            gap: 2,
            px: 3,
            textAlign: 'center',
          }}
        >
          <Typography sx={{ fontSize: '18px', fontWeight: 700, color: 'text.primary' }}>
            Unable to load this page.
          </Typography>
          <Typography sx={{ fontSize: '14px', color: 'text.secondary' }}>
            The app was updated while this tab was open. Refreshing will load the latest version.
          </Typography>
          <Button variant="contained" onClick={this.handleRefresh}>
            Refresh page
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ChunkErrorBoundary;
